import { useCallback, useMemo, useState } from 'react';
import type { HelperField } from '../../utils/helperSchema';
import { useHebrewSetupPreference } from './useHebrewSetupPreference';

export const HEBREW_SETUP_SUGGESTION_FIELD = 'hebrew_setup_suggestion';

export const HEBREW_SETUP_SIDEKICK_CONTEXT = `When setting up a bot, understand the intended callers and spoken language from the user's request. If the bot will speak Hebrew or serve an Israeli audience, use update_field with field_id="hebrew_setup_suggestion" and value="review" to offer the Hebrew template in chat. For full setup, do this after auto_configure completes. The interface itself writes the recommendation and the clickable "Review Hebrew template" button. Call this action after any substantive answer or configuration work; then say nothing about the recommendation or its UI. Never add a follow-up such as "I added a review button", "the button is in your chat panel", or another summary combining a language change with the template offer. The interface already explains the offer. Preserve any substantive answer the user requested, but omit this UI acknowledgement. The user can click it to open a dialog and choose which settings to apply. Do not repeat the recommendation, invent a link, ask for a yes/no reply, or say a dialog is already open. This action does not open the dialog or apply settings. Do not say that the template was enabled, and do not apply its settings yourself. Speaking Hebrew to you does not by itself mean the bot is for Hebrew speakers: use the intended audience, not the operator's language. If this review has already been offered, do not offer it again; its chat button remains available, and the user can also find the Hebrew template on the Templates page.`;

/** Structured signals only: the operator's chat language is not a signal. */
export function isHebrewSetupSuggestionUpdate(fieldId: string, value: string | number | null): boolean {
  if (fieldId === HEBREW_SETUP_SUGGESTION_FIELD) return value === 'review';
  if (typeof value !== 'string') return false;
  const normalized = value.trim().toLowerCase();
  return (fieldId === 'default_country' && normalized === 'il') ||
    (fieldId === 'default_locale' && ['he', 'he-il'].includes(normalized));
}

/** A schema-only action, consumed here rather than written into BotConfig. */
export function useHebrewSetupSuggestion(botId: string) {
  const preference = useHebrewSetupPreference(botId);
  const [offeredForBots, setOfferedForBots] = useState(() => new Set<string>());
  const [openForBot, setOpenForBot] = useState<string | null>(null);

  const offerReview = useCallback(() => {
    setOfferedForBots(previous => previous.has(botId) ? previous : new Set(previous).add(botId));
  }, [botId]);

  const clearOffer = useCallback(() => {
    setOfferedForBots(previous => {
      const next = new Set(previous);
      next.delete(botId);
      return next;
    });
    setOpenForBot(current => current === botId ? null : current);
  }, [botId]);

  const openReview = useCallback(() => {
    // A saved chat link remains a deliberate way to reopen the review.
    if (botId && (offeredForBots.has(botId) || (preference.ready && preference.suppressed))) setOpenForBot(botId);
  }, [botId, offeredForBots, preference.ready, preference.suppressed]);

  const handleFieldUpdate = useCallback((fieldId: string, value: string | number | null) => {
    if (fieldId === HEBREW_SETUP_SUGGESTION_FIELD) {
      if (value === 'review') offerReview();
      return true;
    }
    // Country/language fields are also emitted by the normal setup tools.
    if (isHebrewSetupSuggestionUpdate(fieldId, value)) offerReview();
    return false;
  }, [offerReview]);

  const onOpenChange = useCallback((open: boolean) => {
    if (!open) setOpenForBot(null);
  }, []);

  const offered = preference.ready && !preference.suppressed && offeredForBots.has(botId);
  const field = useMemo<HelperField>(() => ({
    id: HEBREW_SETUP_SUGGESTION_FIELD,
    label: 'Hebrew template review',
    type: 'select',
    description: 'Adds a Hebrew template recommendation and review button to chat; does not open a dialog or change the bot.',
    validation: { options: ['review', 'already_offered', 'suppressed'] },
    currentValue: preference.ready && preference.suppressed ? 'suppressed' : offered ? 'already_offered' : undefined,
  }), [offered, preference.ready, preference.suppressed]);

  return { open: openForBot === botId, openReview, clearOffer, onOpenChange, handleFieldUpdate, field, offered, suppressed: preference.ready && preference.suppressed };
}
